import React, { useState, useEffect, useRef } from "react";
import { searchUsers } from "../../api/api";
import { defaultPfpUrl } from "../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

const UPDATING_VERBS = [
  "Searching",
  "Looking",
  "Sniffing",
  "Probing",
  "Thinking",
  "Vibing",
]

/**
 * Search for users and build a selection list for new or existing chats.
 * @param {Object} props
 * @param {number} props.userId - Current user ID
 * @param {function} props.onSubmit - Called with selected users; may return a Promise
 * @param {Array<{ id: number }>} [props.excludeUsers]
 * @param {string} [props.submitText]
 * @param {(count: number) => void} [props.onSelectionSizeChange] - Fires when the number of selected users changes (for parent layout).
 * @param {Array<object>} [props.candidateUsers] - When set, search is limited to this pool (org affiliates).
 * @param {boolean} [props.autoFocus=true] - Autofocus the search input when mounted.
 * @returns {JSX.Element}
 */
function MultiUserSearch({
  userId,
  onSubmit,
  excludeUsers = [],
  submitText = "Submit",
  onSelectionSizeChange,
  candidateUsers,
  autoFocus = true,
}) {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  /** True while a debounced request is in flight; previous results stay visible until replaced. */
  const [fetching, setFetching] = useState(false);
  const [creating, setCreating] = useState(false);

  const selectedUsersRef = useRef(selectedUsers);
  selectedUsersRef.current = selectedUsers;

  const excludeUsersRef = useRef(excludeUsers);
  excludeUsersRef.current = excludeUsers;

  const candidateUsersRef = useRef(candidateUsers);
  candidateUsersRef.current = candidateUsers;

  const excludeKey = excludeUsers
    .map((u) => u.id)
    .sort((a, b) => a - b)
    .join(",");

  const candidateKey = Array.isArray(candidateUsers)
    ? candidateUsers
        .map((u) => u.id)
        .sort((a, b) => a - b)
        .join(",")
    : "";

  const searchRequestIdRef = useRef(0);
  const searchQueryRef = useRef(searchQuery);
  searchQueryRef.current = searchQuery;

  const onSelectionSizeChangeRef = useRef(onSelectionSizeChange);
  onSelectionSizeChangeRef.current = onSelectionSizeChange;

  useEffect(() => {
    onSelectionSizeChangeRef.current?.(selectedUsers.length);
  }, [selectedUsers.length]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      searchRequestIdRef.current += 1;
      setSearchResults([]);
      setFetching(false);
      return;
    }

    setFetching(true);
    const requestId = ++searchRequestIdRef.current;
    const trimmedAtSchedule = searchQuery.trim();

    const timeoutId = setTimeout(async () => {
      try {
        const excludeIds = new Set(excludeUsersRef.current.map((u) => u.id));
        excludeIds.add(userId);
        const selectedIds = new Set(selectedUsersRef.current.map((u) => u.id));
        const pool = candidateUsersRef.current;

        let users;
        if (Array.isArray(pool)) {
          const q = trimmedAtSchedule.toLowerCase();
          users = pool.filter((item) => {
            if (!item || excludeIds.has(item.id) || selectedIds.has(item.id) || item.id === userId) {
              return false;
            }
            const name = `${item.first_name || ""} ${item.last_name || ""}`.toLowerCase();
            const username = (item.username || "").toLowerCase();
            const email = (item.email || "").toLowerCase();
            return name.includes(q) || username.includes(q) || email.includes(q);
          });
        } else {
          const res = await searchUsers(userId, trimmedAtSchedule);
          if (requestId !== searchRequestIdRef.current) {
            return;
          }
          if (trimmedAtSchedule !== searchQueryRef.current.trim()) {
            return;
          }
          if (res.success && res.data) {
            users = res.data.filter(
              (item) =>
                item.type === "user" &&
                item.id !== userId &&
                !excludeIds.has(item.id) &&
                !selectedIds.has(item.id)
            );
          } else {
            users = [];
          }
        }

        if (requestId !== searchRequestIdRef.current) {
          return;
        }
        if (trimmedAtSchedule !== searchQueryRef.current.trim()) {
          return;
        }
        setSearchResults((users || []).slice(0, 8));
      } catch (error) {
        console.error("Search error:", error);
        if (requestId === searchRequestIdRef.current) {
          setSearchResults([]);
        }
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setFetching(false);
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, userId, excludeKey, candidateKey]);

  const showInitialSearchMessage = fetching && searchResults.length === 0;
  const showResultsUpdating = fetching && searchResults.length > 0;

  const handleUserSelect = (user) => {
    if (selectedUsers.some((u) => u.id === user.id)) {
      return;
    }
    setSelectedUsers([...selectedUsers, user]);
    setSearchResults([...searchResults.filter((u) => u.id !== user.id)]);
  };

  const handleRemoveUser = (userIdToRemove) => {
    setSelectedUsers(selectedUsers.filter((u) => u.id !== userIdToRemove));
  };

  const handleSubmit = async () => {
    if (selectedUsers.length === 0 || creating) return;
    setCreating(true);
    try {
      await Promise.resolve(onSubmit(selectedUsers));
    } finally {
      setCreating(false);
    }
  };

  const displayName = (user) =>
    user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.username || "User";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {selectedUsers.length > 0 && (
        <div className="max-h-32 shrink-0 overflow-y-auto border-b border-ui-border p-4 list-scrollbar">
          <div className="flex flex-wrap gap-2">
            {selectedUsers.map((user) => (
              <div
                key={user.id}
                className="flex items-center gap-2 rounded-full border border-ui-border bg-ui-muted px-2 py-1 pr-1"
              >
                <Avatar className="h-6 w-6">
                  <AvatarImage
                    src={user.pfp_url || defaultPfpUrl}
                    alt={user.username || "User"}
                    onError={(e) => {
                      e.target.src = defaultPfpUrl;
                    }}
                  />
                  <AvatarFallback className="text-[10px]">
                    {displayName(user).slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="max-w-[10rem] truncate text-sm text-ui-foreground">
                  {displayName(user)}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 text-ui-muted-foreground"
                  aria-label={`Remove ${displayName(user)}`}
                  onClick={() => handleRemoveUser(user.id)}
                >
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="shrink-0 border-b border-ui-border p-4">
        <input
          type="text"
          placeholder="Search users…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-md border border-ui-border bg-ui-card px-4 py-2 text-sm text-ui-foreground placeholder:text-ui-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui-background"
          autoFocus={autoFocus}
        />
      </div>

      <div
        className="relative min-h-0 flex-1 overflow-y-auto list-scrollbar"
        style={{ minHeight: "min(14rem, 38vh)" }}
      >
        {showInitialSearchMessage && (
          <div className="p-4 text-center text-sm text-ui-muted-foreground">
            Searching...
          </div>
        )}

        {!showInitialSearchMessage && searchQuery.trim() && searchResults.length === 0 && (
          <div className="p-4 text-center text-sm text-ui-muted-foreground">
            No users found
          </div>
        )}

        {!showInitialSearchMessage && searchQuery.trim() === "" && (
          <div className="p-4 text-center text-sm text-ui-muted-foreground">
            Start typing to search for users
          </div>
        )}

        {showResultsUpdating && (
          <div
            className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center bg-gradient-to-b from-ui-background/90 to-transparent py-2"
            aria-live="polite"
          >
            <span className="rounded-full border border-ui-border bg-ui-card px-3 py-1 text-xs text-ui-muted-foreground shadow-sm">
              {UPDATING_VERBS[Math.floor(Math.random() * UPDATING_VERBS.length)] + "..."}
            </span>
          </div>
        )}

        {searchResults.slice(0, 8).map((user) => (
          <button
            key={user.id}
            type="button"
            onClick={() => handleUserSelect(user)}
            className={cn(
              "flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors",
              showResultsUpdating && "opacity-60",
              "hover:bg-ui-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ui-ring"
            )}
          >
            <Avatar className="h-10 w-10 shrink-0">
              <AvatarImage
                src={user.pfp_url || defaultPfpUrl}
                alt={user.username || "User"}
                onError={(e) => {
                  e.target.src = defaultPfpUrl;
                }}
              />
              <AvatarFallback>{displayName(user).slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-ui-foreground">{displayName(user)}</div>
              {user.username && (user.first_name || user.last_name) && (
                <div className="truncate text-sm text-ui-muted-foreground">@{user.username}</div>
              )}
            </div>
          </button>
        ))}
      </div>

      <div className="shrink-0 border-t border-ui-border p-4">
        <Button
          type="button"
          className="w-full"
          disabled={selectedUsers.length === 0 || creating}
          onClick={handleSubmit}
        >
          {creating ? "Creating…" : submitText}
        </Button>
      </div>
    </div>
  );
}

export default MultiUserSearch;
